/*
 * Mapplic - Custom Interactive Map Plugin
 * Version 5.0.3-nwz (TypeScript Build)
 * https://www.mapplic.com/
 */

import { Sidebar } from './components/Sidebar';
import { Tooltip } from './components/Tooltip';
import { ZoomButtons } from './components/ZoomButtons';
import { LevelSwitcher } from './components/LevelSwitcher';
import { MapViewport, type Space } from './components/MapViewport';
import { UrlState } from './services/UrlState';
import { getColor } from './utils/getColor';
import { div, anchor, span } from './utils/dom';
import { parseMapData } from './utils/parseMapData';
import { ICON_SAD_SMILEY } from './constants/icons';
import type {
  MapplicLocalization,
  Location,
  MapData,
  Level,
  CategoriesMap,
} from './types';
import { MapplicError } from './utils/MapplicError';

// Declare global for WordPress localization
declare const mapplic_localization: MapplicLocalization | undefined;

interface MapplicOptions {
  selector: string;
  searchfields: string[];
  maxScale: number;
  zoomMargin: number;
  zoomSteps: number;
}

const DEFAULT_OPTIONS: MapplicOptions = {
  selector: '[id^=landmark] > *, svg > #items > *',
  searchfields: ['title', 'about'],
  maxScale: 4,
  zoomMargin: 20,
  zoomSteps: 3,
};

const DEFAULT_LOCALIZATION: MapplicLocalization = {
  more: 'Shop ansehen',
  search: 'Suchen ...',
};

export class NwzMapplic {
  // Options and localization
  private options: MapplicOptions = DEFAULT_OPTIONS;
  private l10n: MapplicLocalization = DEFAULT_LOCALIZATION;

  // DOM elements
  public readonly rootElem: HTMLElement;

  // Data
  public categories: CategoriesMap = {};
  public locations: Record<string, Location> = {};
  public currentLocation: Location | null = null;

  // Dimensions
  private contentWidth = 0;
  private contentHeight = 0;
  private levelInsets: Record<string, Space> = {};
  private currentLevel: string | null = null;

  // Zoom state – set to true when the user manually zooms (buttons / keyboard)
  private userHasZoomed = false;

  // Components
  public viewport!: MapViewport;
  public sidebar!: Sidebar;
  private tooltip!: Tooltip;
  private zoombuttons!: ZoomButtons;
  private levelswitcher?: LevelSwitcher;
  private urlState!: UrlState;
  public clear: HTMLButtonElement | null = null;

  constructor(element: HTMLElement, options?: Partial<MapplicOptions>) {
    this.rootElem = element;

    const mapdata = this.rootElem.dataset.mapdata;
    if (!mapdata) {
      return;
    }
  
    this.options = { ...DEFAULT_OPTIONS, ...options } as MapplicOptions;
    this.l10n = { ...DEFAULT_LOCALIZATION };

    this.init(mapdata);
  }

  public async init(mapdata: string): Promise<void> {
    try {
      if (typeof mapplic_localization !== 'undefined') {
        this.l10n = { ...this.l10n, ...mapplic_localization };
      }

      this.rootElem.classList.add('mapplic-element', 'mapplic-loading');

      const data = parseMapData(JSON.parse(mapdata));
      this.initOptions(data);

      delete this.rootElem.dataset.mapdata;

      // Initialize components
      this.initViewport();
      const { shownLevel } = await this.processLevels(data);
      this.initSidebar();
      this.initTooltip();
      this.initLevelSwitcher(data.levels, shownLevel);
      this.initZoomButtons();
      this.initUrlState();
      this.initExternalLinks();

      // Finalize
      this.viewport.addControls();
      this.mapReady();
    
    } catch (error) {
      this.showError(error instanceof MapplicError ? error.userMessage : 'Ein unbekannter Fehler ist aufgetreten');
      console.error('Mapplic initialization error:', error);
    } finally {
      this.rootElem.classList.remove('mapplic-loading');
    }
  }

  private showError(message: string): void {
    // this.el.classList.add('mapplic-error');
    
    const errorDiv = div({
      className: 'mapplic-error',
      attributes: {
        role: 'alert',
        ariaLive: 'assertive'
      },
      html: `
        <div class="mapplic-error__icon" aria-hidden="true">${ICON_SAD_SMILEY}</div>
        <strong class="mapplic-error__title">Hoppla! Ein Fehler ist aufgetreten</strong>
        <p class="mapplic-error__message">${message}</p>
      `
    });
    
    this.rootElem.innerHTML = '';
    this.rootElem.appendChild(errorDiv);
  }

  private initOptions(data: MapData): void {

    this.categories = data.categories;
    this.locations = data.locations;
    this.contentWidth = data.mapwidth;
    this.contentHeight = data.mapheight;

    if (data.options.zoomMargin) {
      this.options.zoomMargin = data.options.zoomMargin;
    }
    if (data.options.zoomSteps) {
      this.options.zoomSteps = data.options.zoomSteps;
    }
    if (data.options.maxScale) {
      this.options.maxScale = data.options.maxScale;
    }
  }

  private initViewport(): void {
    this.viewport = new MapViewport(
      {
        contentWidth: this.contentWidth,
        contentHeight: this.contentHeight,
        maxScale: this.options.maxScale,
        zoomSteps: this.options.zoomSteps,
        zoomMargin: this.options.zoomMargin
      },
      {
        onZoomChange: (scale: number) => {
          this.zoombuttons.update(scale);
        },
        onManualZoom: () => {
          this.userHasZoomed = true;
        },
        switchLevel: (level: string) => {
          this.switchLevel(level);
        }
      }
    );
    this.rootElem.appendChild(this.viewport.rootElem);
  }

  private initSidebar(): void {
    this.sidebar = new Sidebar(
      {
        searchfields: ['title', 'about']
      },
      this.l10n,
      {
        onLocationClick: (id: string, triggerElement?: HTMLElement) => {
          this.showLocation(id, false, triggerElement);
        },
        getLocationColor: (location) => getColor(location, this.categories),
        getContainerElement: () => this.viewport?.rootElem ?? null,
      },
      this.categories,
      this.locations,
    );
    this.rootElem.prepend(this.sidebar.rootElem);
  }

  private async processLevels(data: MapData): Promise<{ shownLevel: string | null }> {
    
    let shownLevel: string | null = null;

    const loadPromises = data.levels.map(async (level) => {
      const layer = div({
        className: 'mapplic-layer',
        attributes: {
          role: 'img',
          'aria-label': `Lageplan ${level.title}`
        }
      });
      this.viewport.addLayer(level.id, layer);

      await this.loadSvgLevel(level.map, level, layer);

      if (!shownLevel || level.show) {
        shownLevel = level.id;
      }
    });

    await Promise.all(loadPromises);

    return { shownLevel };
  }

  private async loadSvgLevel(source: string, level: Level, layer: HTMLElement): Promise<void> {
    const svgContainer = div({ className: 'mapplic-map-image' });

    try {
      const response = await fetch(source);
      if (!response.ok) {
        throw new MapplicError(
          `Failed to load SVG map: ${response.status} ${response.statusText}`,
          'network',
          `Die Kartengrafik für "${level.title || level.id}" konnte nicht geladen werden (HTTP ${response.status}).`
        );
      }
      const svgContent = await response.text();

      svgContainer.innerHTML = svgContent;
      const svgElement = svgContainer.querySelector('svg');
      if (svgElement) this.scopeSvgIds(svgElement, level.id);
      this.levelInsets[level.id] = this.computeContentInsets(svgElement);
      
      layer.appendChild(svgContainer);
      const levelLocations = Object.values(this.locations).filter(l => l.level === level.id);
      this.setupLocationsDOM(levelLocations);
      this.setupSvgInteractivity(svgContainer);

    } catch (error) {
      if (error instanceof MapplicError) throw error;
      throw new MapplicError(
        error instanceof Error ? error.message : 'Unknown error',
        'network',
        `Fehler beim Laden der Kartengrafik für "${level.title || level.id}".`
      );
    }
  }

  /**
   * Suffix all IDs in the SVG with the level id (e.g. "_eg") to prevent
   * collisions when multiple SVG levels are inlined into the same DOM.
   *
   * Exception: direct children of landmark groups ([id^=landmark], #items)
   * keep their original IDs because Mapplic uses them for location matching.
   */
  private scopeSvgIds(svgElement: SVGSVGElement, levelId: string): void {
    const suffix = `_${levelId}`;

    // Collect IDs of location elements that Mapplic must find by their
    // original ID via the selector '[id^=landmark] > *[id="…"]'
    const locationIds = new Set<string>();
    svgElement.querySelectorAll('[id^=landmark] > [id], #items > [id]').forEach((el) => {
      locationIds.add(el.id);
    });

    // Collect all other IDs that will be suffixed — including the <svg> root itself
    const scopedIds = new Set<string>();
    const allElements: Element[] = [svgElement, ...Array.from(svgElement.querySelectorAll('[id]'))];
    allElements.forEach((el) => {
      if (el.id && !locationIds.has(el.id)) {
        scopedIds.add(el.id);
      }
    });

    if (scopedIds.size === 0) return;

    // Rename the IDs
    allElements.forEach((el) => {
      if (el.id && scopedIds.has(el.id)) {
        el.id = el.id + suffix;
      }
    });

    // Update all internal references that pointed to a now-renamed ID
    svgElement.querySelectorAll('*').forEach((el) => {
      for (const attr of Array.from(el.attributes)) {
        // url(#id) — clip-path, filter, fill, mask, marker, etc.
        if (attr.value.includes('url(#')) {
          attr.value = attr.value.replace(/url\(#([^)]+)\)/g, (match, id) =>
            scopedIds.has(id) ? `url(#${id}${suffix})` : match
          );
        }
        // href="#id" and xlink:href="#id" — <use> elements
        if ((attr.name === 'href' || attr.name === 'xlink:href') && attr.value.startsWith('#')) {
          const id = attr.value.slice(1);
          if (scopedIds.has(id)) attr.value = `#${id}${suffix}`;
        }
      }
    });
  }

  /**
   * Compute the gap (in content-space pixels) between the full
   * coordinate space (contentWidth × contentHeight) and the actual
   * SVG artwork that is scaled to fit inside it.
   * The MapViewport uses this remove the gap from the edges when calculating zoom/pan
   */
  private computeContentInsets(svgElement: SVGSVGElement | null): Space {
    const noInsets: Space = { left: 0, right: 0, top: 0, bottom: 0 };
    if (!svgElement) return noInsets;

    // Prefer viewBox, fall back to width/height attributes
    const vb = svgElement.viewBox.baseVal;
    const svgW = vb.width || parseFloat(svgElement.getAttribute('width') || '0');
    const svgH = vb.height || parseFloat(svgElement.getAttribute('height') || '0');
    if (!svgW || !svgH) return noInsets;

    const containerAR = this.contentWidth / this.contentHeight;
    const svgAR = svgW / svgH;

    if (svgAR < containerAR) {
      // SVG is narrower → vertical fit, horizontal gaps
      const effectiveWidth = this.contentHeight * svgAR;
      const gap = (this.contentWidth - effectiveWidth) / 2;
      return { left: gap, right: gap, top: 0, bottom: 0 };
    } else if (svgAR > containerAR) {
      // SVG is wider → horizontal fit, vertical gaps
      const effectiveHeight = this.contentWidth / svgAR;
      const gap = (this.contentHeight - effectiveHeight) / 2;
      return { left: 0, right: 0, top: gap, bottom: gap };
    }

    return noInsets;
  }

  private setupSvgInteractivity(svgContainer: HTMLElement): void {
    // Click/touch handler (keyboard access is provided via sidebar)
    const handleSvgClick = (e: Event) => {
      if (this.viewport.dragging) return;
      const target = e.currentTarget as Element;
      this.showLocation(target.getAttribute('id') || '');
    };

    svgContainer.querySelectorAll(this.options.selector).forEach((el) => {
      el.addEventListener('click', handleSvgClick);
    });
  }

  private initZoomButtons(): void {
    this.zoombuttons = new ZoomButtons({
      onZoomIn: () => { this.userHasZoomed = true; return this.viewport.zoomIn(); },
      onZoomOut: () => { this.userHasZoomed = true; return this.viewport.zoomOut(); },
      getFitScale: () => this.viewport.fitScale,
      getMaxScale: () => this.options.maxScale
    });
    this.viewport.addControl(this.zoombuttons.rootElem);
  }

  private initLevelSwitcher(levels: Level[], shownLevel: string | null): void {
    if (levels.length > 1) {
      this.levelswitcher = new LevelSwitcher(
        levels,
        shownLevel || '',
        (levelId: string) => {
          this.switchLevel(levelId);
        }
      );
      this.viewport.addControl(this.levelswitcher.rootElem);
    }
    this.switchLevel(shownLevel);
  }

  private initUrlState(): void {
    this.urlState = new UrlState({
      showLocation: (id: string, isDeeplink: boolean) => {
        this.showLocation(id, isDeeplink);
      }
    });
  }

  /* External links handler: Listens for clicks
   * on elements with class "mapplic-location" 
   * and shows the corresponding location tooltip.
   * Not needed rigth now.
  */
  private initExternalLinks(): void {
    document.addEventListener('click', (e: MouseEvent) => {
      const target = (e.target as Element).closest('.mapplic-location');
      if (target) {
        e.preventDefault();
        const href = target.getAttribute('href');
        if (href) this.showLocation(href.substring(1));
      }
    });
  }

  private initTooltip(): void {
    this.tooltip = new Tooltip(
      { linkNewTab: false },
      this.l10n,
      {
        onClose: () => {
          this.hideLocation();
        },
        getColor: (location) => getColor(location, this.categories)
      }
    );
    this.viewport.addToMap(this.tooltip.rootElem);
  }

  private showTooltip(location: Location, triggerElement?: HTMLElement | null): void {
    // Set trigger element for focus return
    this.tooltip.setTriggerElement(triggerElement || null);

    const wasHidden = !this.tooltip.isVisible();

    // Calculate duration once, used for both tooltip translate and viewport move
    const duration = this.calcMoveDuration(location);

    // Set --zoom-duration BEFORE positioning so the tooltip's CSS transition
    // picks up the same duration as the map's transform transition
    this.viewport.setZoomDuration(duration);

    // Content
    this.tooltip.setLocation(location);

    // Position tooltip in content-pixel space
    this.tooltip.setPosition(
      location.x * this.contentWidth,
      location.y * this.contentHeight
    );

    // Show tooltip (instant = no slide animation on first appearance)
    this.tooltip.show(wasHidden);

    // Zoom after positioning
    this.zoomToTooltip(location, duration);
  }

  private calcMoveDuration(location: Location): number {
    const minDurationMs = 300;
    const maxDurationMs = 900;
    const pxPerMs = 0.8; // Constant speed: 80px in 100ms, 800px in 1s

    const vp = this.viewport;
    const currentCenterX = (vp.rootElem.offsetWidth * 0.5 - vp.x) / (vp.scale * this.contentWidth);
    const currentCenterY = (vp.rootElem.offsetHeight * 0.5 - vp.y) / (vp.scale * this.contentHeight);
    const normalizedDist = Math.sqrt(
      (location.x - currentCenterX) ** 2 +
      (location.y - currentCenterY) ** 2
    );
    const pixelDist = normalizedDist * vp.scale * Math.max(this.contentWidth, this.contentHeight);
    return Math.round(Math.max(minDurationMs, Math.min(maxDurationMs, pixelDist / pxPerMs)));
  }

  private zoomToTooltip(location: Location, duration?: number): void {
    // Keep current zoom if user has manually zoomed, otherwise use step 2
    const zoomStep = this.userHasZoomed ? null : Math.min(2, this.options.zoomSteps);
    const dimensions = this.tooltip.getDimensions();

    this.viewport.setView(location.x, location.y, {
      zoomStep,
      duration: duration ?? this.calcMoveDuration(location),
      verticalOffset: dimensions.height / 4,
      preserveSpace: {
        top: dimensions.height,
        right: dimensions.width / 2,
        bottom: 0,
        left: dimensions.width / 2
      }
    });
  }

  private hideTooltip(): void {
    this.tooltip.hide();
    this.viewport.setPreserveArea(null);
  }

  private mapReady(): void {
    this.viewport.init();
    this.sidebar.sort();
    this.urlState.check();
  }

  public switchLevel(target: string | null): void {
    if (!target || target === this.currentLevel) return;

    const layer = this.viewport.showLayer(target);
    if (!layer) return;

    this.currentLevel = target;

    // Hide tooltip
    if (this.tooltip) this.hideTooltip();

    // Apply content insets for this level
    const insets = this.levelInsets[target];
    if (insets) {
      this.viewport.setContentInsets(insets);
    }

    // Update LevelSwitcher UI
    this.levelswitcher?.setLevel(target);
  }

  private setupLocationsDOM(locations: Location[]): void {
    locations.forEach((location) => {
      // Interactive element (mouse/touch only, keyboard access via sidebar)
      const elem = this.viewport.queryMap<HTMLElement>(`[id^=landmark] > *[id="${location.id}"]`);
      if (elem) {
        elem.setAttribute('class', 'mapplic-clickable');
        elem.setAttribute('data-location', location.id);
        elem.setAttribute('data-category', String(location.category || ''));
        location.elem = elem;

        if (location.fill) {
          elem.setAttribute('fill', location.fill);
          elem.querySelectorAll(':scope > *').forEach((child) => {
            child.setAttribute('fill', location.fill!);
          });
        }
      }

      // Marker
      if (!location.pin?.includes('hidden')) {
        const levelEl = this.viewport.getLayer(location.level || '');
        if (levelEl) {
          const pin = anchor({
            className: 'mapplic-pin',
            attributes: { href: '#', tabindex: '-1' }
          });
          pin.style.top = `${location.y * 100}%`;
          pin.style.left = `${location.x * 100}%`;

          pin.addEventListener('click', (e) => {
            e.preventDefault();
            this.showLocation(location.id);
          });

          const titleSpan = span({ html: `<span>${location.title}</span>` });
          pin.appendChild(titleSpan);

          if (location.fill) {
            pin.style.backgroundColor = location.fill;
            pin.style.borderColor = location.fill;
          }

          if (location.category) {
            pin.setAttribute('data-category', location.category[0]);
          }

          pin.setAttribute('data-location', location.id);
          levelEl.appendChild(pin);
          location.elem = pin;
        }
      }

      if (location.elem && location.pin) {
        const pinClass = (location.pin || '').replace('hidden', '').trim();
        location.elem.classList.add(...pinClass.split(' ').filter(c => c));
      }
    });
  }

  public showLocation(id: string, check?: boolean, triggerElement?: HTMLElement): void {
    const location = this.locations[id];
    if (!location) return;
    if (location.action === 'disabled') return;

    // Already showing this location — just update trigger element for focus return
    if (this.currentLocation?.id === id && this.tooltip.getLocation()?.id === id) {
      this.tooltip.setTriggerElement(triggerElement || null);
      return;
    }

    this.currentLocation = location;

    this.switchLevel(location.level || null);
    this.showTooltip(location, triggerElement);

    // Active state
    this.rootElem.querySelectorAll('.mapplic-active').forEach((el) => {
      el.classList.remove('mapplic-active');
    });
    if (location.elem && location.elem.classList) {
      location.elem.classList.add('mapplic-active');
    }
    if (location.listElem) {
      location.listElem?.classList.add('mapplic-active');
    }

    // Deeplinking
    if (this.urlState && !check) {
      this.urlState.update(id);
    }
  }

  public hideLocation(): void {
    this.rootElem.querySelectorAll('.mapplic-active').forEach((el) => {
      el.classList.remove('mapplic-active');
    });
    this.urlState?.clear();
    if (this.tooltip) this.hideTooltip();
    this.viewport.setPreserveArea(null);
    this.currentLocation = null;
  }
}
